<template>
  <div class="block-chart">
    <!-- The canvas is a picture of the numbers, so screen readers skip it and
         read the table below, which stays in the DOM (visually hidden) while
         the chart shows and becomes visible if the chart can't be drawn. -->
    <div
      v-if="!failed"
      class="block-chart__canvas-box"
      :class="{ 'block-chart__canvas-box--pie': chart.chartType === 'pie' }"
      aria-hidden="true"
    >
      <canvas ref="canvas" />
    </div>
    <chat-table-block :class="{ 'block-chart__sr-only': !failed }" :table="accessibleTable" />
  </div>
</template>

<script>
import ChatTableBlock from './ChatTableBlock.vue';
import { buildChartConfig, loadChart, tableFromChart } from './chat-chart';

// Draws a normalized chart (see chartOf in chat-structured.js) on a canvas.
// The same data is always rendered as a table too: visually hidden for
// screen readers while the chart shows, and shown instead of it if Chart.js
// can't be loaded or throws, so every customer can get to the numbers.
export default {
  name: 'ChatChartBlock',
  components: { ChatTableBlock },
  props: {
    chart: {
      type: Object,
      required: true,
    },
    table: {
      type: Object,
      default: null,
    },
  },
  data() {
    return { failed: false };
  },
  computed: {
    // The block's own table when it has one, else one built from the chart data.
    accessibleTable() {
      return this.table || tableFromChart(this.chart);
    },
  },
  watch: {
    chart() {
      this.failed = false;
      this.$nextTick(this.draw);
    },
  },
  mounted() {
    this.draw();
  },
  beforeDestroy() {
    this.destroyChart();
  },
  methods: {
    async draw() {
      let Chart;
      try {
        Chart = await loadChart();
      } catch (error) {
        this.failed = true;
        return;
      }
      // The message may have been re-rendered or closed while the chunk loaded.
      const { canvas } = this.$refs;
      if (this._isDestroyed || !canvas) { return; }
      this.destroyChart();
      try {
        const { fontFamily } = window.getComputedStyle(this.$el);
        this.chartInstance = new Chart(canvas, buildChartConfig(this.chart, { fontFamily }));
      } catch (error) {
        this.failed = true;
      }
    },
    destroyChart() {
      if (this.chartInstance) {
        this.chartInstance.destroy();
        this.chartInstance = null;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.block-chart {
  position: relative;
  max-width: 100%;
}

.block-chart__canvas-box {
  position: relative;
  height: 240px;
  padding: 12px;
  background-color: #ffffff;
  border: 1px solid rgba(32, 33, 37, 0.12);
  border-radius: 12px;
}

/* A pie shares its width with the legend beside it, so it gets more height. */
.block-chart__canvas-box--pie {
  height: 300px;
}

/* Hidden on screen but still in the accessibility tree. */
.block-chart__sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}
</style>
